// src/components/auth/EmailVerificationModal.js

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { resendVerificationEmail, checkEmailVerification } from '../../firebase/auth';

export default function EmailVerificationModal({
  isOpen,
  onClose,
  userEmail,
  onVerificationComplete
}) {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [lastSentTime, setLastSentTime] = useState(null);
  const [cooldownTime, setCooldownTime] = useState(0);

  // Refs for cleanup
  const messageTimeoutRef = useRef(null);
  const cooldownIntervalRef = useRef(null);
  const verificationTimeoutRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (messageTimeoutRef.current) clearTimeout(messageTimeoutRef.current);
      if (cooldownIntervalRef.current) clearInterval(cooldownIntervalRef.current);
      if (verificationTimeoutRef.current) clearTimeout(verificationTimeoutRef.current);
    };
  }, []);

  const showMessage = useCallback((text, type = 'info') => {
    // Clear any existing timeout
    if (messageTimeoutRef.current) clearTimeout(messageTimeoutRef.current);

    setMessage({ text, type });
    messageTimeoutRef.current = setTimeout(() => setMessage({ text: '', type: '' }), 5000);
  }, []);

  const handleResendEmail = async () => {
    // Implement cooldown to prevent spam
    const now = Date.now();
    const cooldownPeriod = 60000; // 1 minute

    if (lastSentTime && (now - lastSentTime) < cooldownPeriod) {
      const remainingTime = Math.ceil((cooldownPeriod - (now - lastSentTime)) / 1000);
      showMessage(`Please wait ${remainingTime} seconds before requesting another email.`, 'warning');
      return;
    }

    setLoading(true);
    try {
      const { error } = await resendVerificationEmail();

      if (error) {
        showMessage(`Failed to send verification email: ${error.message}`, 'error');
      } else {
        setLastSentTime(now);
        showMessage('Verification email sent! Check your inbox and spam folder.', 'success');

        // Start cooldown timer - clear any existing interval first
        if (cooldownIntervalRef.current) clearInterval(cooldownIntervalRef.current);
        setCooldownTime(60);
        cooldownIntervalRef.current = setInterval(() => {
          setCooldownTime(prev => {
            if (prev <= 1) {
              clearInterval(cooldownIntervalRef.current);
              cooldownIntervalRef.current = null;
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    } catch (error) {
      showMessage('An unexpected error occurred. Please try again.', 'error');
    }
    setLoading(false);
  };

  const handleCheckVerification = async () => {
    setLoading(true);
    try {
      const { isVerified, error } = await checkEmailVerification();

      if (error) {
        showMessage(`Error checking verification: ${error.message}`, 'error');
      } else if (isVerified) {
        showMessage('Email verified successfully!', 'success');
        // Clear any existing timeout
        if (verificationTimeoutRef.current) clearTimeout(verificationTimeoutRef.current);
        verificationTimeoutRef.current = setTimeout(() => {
          onVerificationComplete();
        }, 1500);
      } else {
        showMessage('Email not yet verified. Please check your inbox and click the verification link.', 'warning');
      }
    } catch (error) {
      showMessage('An unexpected error occurred while checking verification.', 'error');
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'var(--bg-modal-overlay)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 10000
    }}>
      <div style={{
        backgroundColor: 'var(--bg-secondary)',
        borderRadius: '8px',
        padding: '32px',
        maxWidth: '500px',
        width: '90%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: 'var(--shadow-lg)'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div style={{
            fontSize: '48px',
            marginBottom: '16px',
            color: '#ffc107'
          }}>
            ✉️
          </div>
          <h2 style={{
            margin: '0 0 8px 0',
            color: 'var(--text-primary)',
            fontSize: '24px',
            fontWeight: 'bold'
          }}>
            Verify Your Email
          </h2>
          <p style={{
            margin: 0,
            color: 'var(--text-secondary)',
            fontSize: '16px'
          }}>
            We sent a verification email to:
          </p>
          <p style={{
            margin: '8px 0 0 0',
            color: 'var(--btn-primary)',
            fontSize: '16px',
            fontWeight: 'bold'
          }}>
            {userEmail}
          </p>
        </div>

        {/* Message Display */}
        {message.text && (
          <div style={{
            padding: '12px 16px',
            marginBottom: '20px',
            borderRadius: '6px',
            backgroundColor:
              message.type === 'error' ? '#f8d7da' :
              message.type === 'success' ? '#d4edda' :
              message.type === 'warning' ? '#fff3cd' : '#d1ecf1',
            color:
              message.type === 'error' ? '#721c24' :
              message.type === 'success' ? '#155724' :
              message.type === 'warning' ? '#856404' : '#0c5460',
            border: `1px solid ${
              message.type === 'error' ? '#f5c6cb' :
              message.type === 'success' ? '#c3e6cb' :
              message.type === 'warning' ? '#ffeaa7' : '#bee5eb'
            }`,
            textAlign: 'center',
            fontSize: '14px'
          }}>
            {message.text}
          </div>
        )}

        {/* Instructions */}
        <div style={{
          backgroundColor: 'var(--bg-tertiary)',
          padding: '20px',
          borderRadius: '6px',
          marginBottom: '24px'
        }}>
          <h3 style={{
            margin: '0 0 12px 0',
            color: 'var(--text-primary)',
            fontSize: '16px'
          }}>
            Next Steps:
          </h3>
          <ol style={{
            margin: 0,
            paddingLeft: '20px',
            color: 'var(--text-secondary)',
            fontSize: '14px',
            lineHeight: '1.5'
          }}>
            <li>Check your email inbox for a message from KC Pool Tournament</li>
            <li>If you don't see it, check your spam/junk folder</li>
            <li>Click the verification link in the email</li>
            <li>Return here and click "I've Verified My Email"</li>
          </ol>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button
            onClick={handleCheckVerification}
            disabled={loading}
            style={{
              flex: 1,
              padding: '12px 20px',
              backgroundColor: loading ? 'var(--btn-secondary)' : 'var(--success)',
              color: 'var(--btn-text, #fff)',
              border: 'none',
              borderRadius: '6px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1,
              minWidth: '150px'
            }}
          >
            {loading ? 'Checking...' : "I've Verified My Email"}
          </button>

          <button
            onClick={handleResendEmail}
            disabled={loading || cooldownTime > 0}
            style={{
              flex: 1,
              padding: '12px 20px',
              backgroundColor: (loading || cooldownTime > 0) ? 'var(--btn-secondary)' : 'var(--info, #17a2b8)',
              color: 'var(--btn-text, #fff)',
              border: 'none',
              borderRadius: '6px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: (loading || cooldownTime > 0) ? 'not-allowed' : 'pointer',
              opacity: (loading || cooldownTime > 0) ? 0.6 : 1,
              minWidth: '150px'
            }}
          >
            {cooldownTime > 0 ? `Resend (${cooldownTime}s)` :
             loading ? 'Sending...' : 'Resend Email'}
          </button>
        </div>

        {/* Close Button */}
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              backgroundColor: 'transparent',
              color: 'var(--btn-secondary)',
              border: '1px solid var(--border-color)',
              borderRadius: '4px',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}